// Picture, the first stage of a timeline picture: the image maker. Paint a picture from words and images to copy from, or
// change one, and every version is kept on the picture's marker. Nothing waits to be confirmed: the newest picture
// painted, or the version or other picture tapped in "Start from", is the one on the timeline straight away. A new
// picture's marker is made when its first picture arrives, at t, under the id the stage page was opened with.
// Painting keeps going if the page is left (ui/jobs.ts) and lands on the marker wherever the episode is.
// The characters' talking faces show on each picture: found from their face dots when it was painted, else carried
// over or spread across it. Moving them is the next stage, Faces.
import { useState } from "react";
import { faceStyleOf } from "../../../library/faceSets";
import { getLibrary, useLibrary } from "../../../library/store";
import { markUsed, refreshRef, startDraft } from "../../../library/drafts";
import { byRecent, findAsset, type Asset, type AssetKind, type Library } from "../../../model/library";
import {
  addPicture, animFaces, carryFaces, markById, marksOf, motionOf, setMarker, spreadFaces,
  type PaintSource, type PictureMark, type PictureVersionFaces, type PlacedFace, type TimelineEpisode,
} from "../../../model/timeline";
import { uid } from "../../../model/ids";
import { api } from "../../api";
import { picJob } from "../../jobs";
import { ImageMaker, sameRef, type MakerGroup, type MakerPainted, type MakerRequest, type MakerStart } from "../../images/ImageMaker";
import { AssetPage } from "../../library/AssetPage";
import { applyToEpisode } from "../episodeStore";
import { useTimeline } from "../TimelineLoader";
import { refCharacterIds } from "./shared";

const GROUPS: { kind: AssetKind; title: string }[] = [
  { kind: "character", title: "Characters" },
  { kind: "background", title: "Places" },
  { kind: "prop", title: "Props" },
];

// A marker's versions, with the faces now on it kept on the version in use, so going back to that version later
// brings them back. A marker from before versions were kept gets its picture as the first one.
export function keptVersions(m: PictureMark): PictureVersionFaces[] {
  if (!m.versions.length) return [{ id: uid("v"), picture: m.picture, how: "drawn", text: m.prompt, at: 0, faces: m.faces }];
  return m.versions.map(v => (v.picture.src === m.picture.src ? { ...v, faces: m.faces } : v));
}

// Where the faces go on a version about to be used: as kept on it, else as found when it was painted, with anyone not
// found where they were on the picture it was made from (scaled if the size differs), and anyone still missing of the
// characters it was painted from spread across it to be dragged into place.
function facesFor(lib: Library, v: PictureVersionFaces, from: PictureMark | undefined, refs: PaintSource[]): PlacedFace[] {
  const known = [...(v.faces ?? [])];
  for (const f of from ? carryFaces(from.faces, from.picture, v.picture) : []) if (!known.some(k => k.char === f.char)) known.push(f);
  const spread = spreadFaces([...new Set(refCharacterIds(lib, refs))], v.picture);
  return [...known, ...spread.filter(f => !known.some(k => k.char === f.char))];
}

// Puts version `v` in use on marker `id`, adding it to the versions if it is new, or makes the marker with it.
function putVersion(e: TimelineEpisode, id: string, t: number, v: PictureVersionFaces, f: { faces: PlacedFace[]; refs: PaintSource[]; style?: string }): TimelineEpisode {
  const m = markById(e, "picture", id);
  const fields = { picture: v.picture, prompt: v.text, refs: f.refs, faces: f.faces, ...(f.style ? { style: f.style } : {}) };
  if (!m) return addPicture(e, t, { ...fields, versions: [v] }, id).ep; // a new picture gets its own placeholder line at a boundary, so it never lands on another one
  const kept = keptVersions(m);
  return setMarker(e, "picture", id, { ...fields, versions: kept.some(x => x.id === v.id) ? kept : [...kept, v] });
}

export function PictureMaker({ t, id, onNote }: { t: number; id: string; onNote: (message: string | null) => void }) {
  const { ep, update } = useTimeline();
  const lib = useLibrary();
  const mark = markById(ep, "picture", id);
  const [draftRefs, setDraftRefs] = useState<PaintSource[]>([]); // until the marker is made
  const refs = mark?.refs ?? draftRefs;
  const pictures = marksOf(ep, "picture");
  const [making, setMaking] = useState<string | null>(null); // a new library asset being made from a "New …" tile
  const [changing, setChanging] = useState<{ id: string; ref: PaintSource } | null>(null); // a picked asset whose form is open (its pencil)

  // Picking who and what is in it changes the marker's references, once there is a marker.
  function setRefs(f: (rs: PaintSource[]) => PaintSource[]) {
    if (!mark) return setDraftRefs(f);
    update(e => { const m = markById(e, "picture", id); return m ? setMarker(e, "picture", id, { refs: f(m.refs) }) : e; }, { merge: `refs-${id}` });
  }

  const assetRef = (a: Asset): PaintSource => ({ src: a.picture.src, name: a.name, kind: a.kind as PaintSource["kind"] });
  const groups: MakerGroup[] = [
    ...GROUPS.map(g => ({ key: g.kind, title: g.title, kind: g.kind, items: lib.assets.filter(a => a.kind === g.kind).sort(byRecent).map(assetRef) })),
    ...(pictures.length ? [{ key: "shot", title: "Other pictures", items: pictures.flatMap((p, i): PaintSource[] => (pictures.findIndex(q => q.picture.src === p.picture.src) === i ? [{ src: p.picture.src, name: `Picture ${i + 1}`, kind: "shot" }] : [])) }] : []),
  ];
  // "Start from": this marker's versions as V1, V2… (newest first), then the other pictures on the timeline.
  const versions = mark ? keptVersions(mark) : [];
  const others = pictures.map((p, i) => ({ p, i })).filter(({ p }) => p.id !== id);
  // Where another picture's animation in use stops: its last frame, with the faces found on it.
  const endOf = (p: PictureMark) => {
    const m = motionOf(p), last = m?.frames[m.frames.length - 1];
    return m && last ? { picture: last.picture, faces: animFaces(p, m)[m.frames.length]! } : undefined;
  };
  const facesOn = (v: PictureVersionFaces) => (mark && v.picture.src === mark.picture.src ? mark.faces : v.faces ?? []);
  const withFaces = (s: Omit<MakerStart, "faces">, faces: PlacedFace[]): MakerStart => ({ ...s, faces: faces.map(f => ({ key: f.char, face: f.face, style: faceStyleOf(f.char) })) });
  const starts: MakerStart[] = [
    ...versions.map((v, i) => withFaces({ id: v.id, label: `V${i + 1}`, picture: v.picture }, facesOn(v))).reverse(),
    ...others.map(({ p, i }) => withFaces({ id: `pic:${p.id}`, label: `Picture ${i + 1}`, picture: p.picture, other: true }, p.faces)),
    ...others.flatMap(({ p, i }) => { const e = endOf(p); return e ? [withFaces({ id: `end:${p.id}`, label: `Picture ${i + 1} · end`, picture: e.picture, other: true }, e.faces)] : []; }),
  ];
  const inUse = mark ? versions.find(v => v.picture.src === mark.picture.src)?.id ?? null : null;

  // The painted picture goes straight onto the marker as its newest version, wherever the episode is by then.
  async function paint({ start, refs, text, style }: MakerRequest): Promise<MakerPainted> {
    markUsed(refs);
    const fromId = start?.id.startsWith("pic:") || start?.id.startsWith("end:") ? start.id.slice(4) : id; // the marker whose faces carry over
    const { faces: found = [], ...picture } = start ? await api.editShot(start.picture.src, refs, text, style, true) : await api.paintShot(refs, text, style, true);
    const lib = getLibrary();
    const charOf = (src: string) => lib.assets.find(a => a.kind === "character" && a.picture.src === src)?.id;
    const faces = found.flatMap(f => { const char = charOf(f.ref); return char ? [{ char, face: f.face }] : []; });
    const v: PictureVersionFaces = { id: uid("v"), picture, how: start ? "changed" : "drawn", text, at: Date.now(), ...(faces.length ? { faces } : {}) };
    let n = 1;
    await applyToEpisode(ep.id, e => {
      const m = markById(e, "picture", id);
      n = (m ? keptVersions(m).length : 0) + 1;
      return putVersion(e, id, t, v, { faces: facesFor(lib, v, markById(e, "picture", fromId), refs), refs, style });
    }, { undoable: true });
    // Anyone whose face wasn't found (or only their eyes were) is a guess to check.
    const sure = found.filter(f => !f.guessed).map(f => charOf(f.ref));
    const guessed = [...new Set(refCharacterIds(lib, refs))].filter(c => !sure.includes(c)).map(c => findAsset(lib, c)?.name ?? "someone");
    const note = guessed.length ? `Couldn't tell where ${guessed.length > 1 ? `${guessed.slice(0, -1).join(", ")} and ${guessed[guessed.length - 1]}'s faces go, so they're guesses` : `${guessed[0]}'s face goes, so it's a guess`}. Open Faces to move ${guessed.length > 1 ? "them" : "it"}.` : undefined;
    return { start: { id: v.id, label: `V${n}`, picture }, ...(note ? { note } : {}) };
  }

  // A tapped start is in use at once: one of this marker's versions, or another picture on the timeline, which joins
  // this marker's versions (its words, references and faces come with it); "Picture 2 · end" is the last frame of
  // another picture's animation, which joins the same way with the faces found on that frame.
  function pick(s: MakerStart) {
    if (s.id.startsWith("pic:")) {
      const src = markById(ep, "picture", s.id.slice(4));
      if (!src) return;
      const same = mark && keptVersions(mark).find(v => v.picture.src === src.picture.src);
      const v: PictureVersionFaces = same ?? { id: uid("v"), picture: src.picture, how: "drawn", text: src.prompt, at: Date.now(), faces: src.faces };
      update(e => putVersion(e, id, t, v, { faces: src.faces, refs: src.refs, style: src.style }));
      return;
    }
    if (s.id.startsWith("end:")) {
      const src = markById(ep, "picture", s.id.slice(4)), e = src && endOf(src);
      if (!src || !e) return;
      const same = mark && keptVersions(mark).find(v => v.picture.src === e.picture.src);
      const v: PictureVersionFaces = same ?? { id: uid("v"), picture: e.picture, how: "drawn", text: src.prompt, at: Date.now(), faces: e.faces };
      update(x => putVersion(x, id, t, v, { faces: e.faces, refs: src.refs, style: src.style }));
      return;
    }
    const v = versions.find(x => x.id === s.id);
    if (!v || !mark || v.picture.src === mark.picture.src) return;
    update(e => putVersion(e, id, t, v, { faces: v.faces ?? facesFor(lib, v, mark, refs), refs, style: mark.style }));
  }

  // The bin on the picked version: it goes from the marker's versions at once (undo brings it back). If it was the one
  // in use, the newest left takes its place. The last version is never deleted (the page hides the bin then).
  function remove(s: MakerStart) {
    update(e => {
      const m = markById(e, "picture", id);
      if (!m) return e;
      const kept = keptVersions(m);
      const gone = kept.find(v => v.id === s.id);
      if (!gone || kept.length < 2) return e;
      const left = kept.filter(v => v.id !== s.id);
      if (gone.picture.src !== m.picture.src) return setMarker(e, "picture", id, { versions: left });
      const v = left[left.length - 1]!;
      return setMarker(e, "picture", id, { picture: v.picture, prompt: v.text, faces: v.faces ?? facesFor(getLibrary(), v, m, m.refs), versions: left });
    });
  }

  // Back from a new asset's form: if its picture has arrived, it is picked as a reference.
  function madeAsset(aid: string) {
    setMaking(null);
    const a = findAsset(getLibrary(), aid);
    if (!a?.picture.src) return;
    const r = assetRef(a);
    setRefs(rs => (rs.some(x => sameRef(x, r)) ? rs : [...rs, r]));
  }

  return (
    <>
      <ImageMaker bare onNote={onNote} draftKey={`picture:${id}:words`} mode="shot" refsTitle="In the picture"
        starts={starts} startId={inUse} jobKey={picJob(id)}
        refs={refs} setRefs={setRefs} groups={groups} paint={paint} onNew={kind => setMaking(startDraft(kind))} onPick={pick} onDelete={remove} face={{}}
        onEdit={r => { const a = lib.assets.find(x => x.kind === r.kind && x.picture.src === r.src); if (a) setChanging({ id: a.id, ref: r }); }} />
      {making && <AssetPage assetId={making} onClose={() => madeAsset(making)} />}
      {changing && <AssetPage assetId={changing.id} onClose={() => { setRefs(rs => refreshRef(rs, changing.ref, changing.id)); setChanging(null); }} />}
    </>
  );
}
